/**
 * Per-client hosting tab — every cPanel/WHM account mapped to this
 * client, aggregated across every WHM server. Same detail set as the
 * main Host Servers page, with a deep-link into the existing
 * HostAccountDetailPage for the full drill-down.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Server, RefreshCw, Loader2, TriangleAlert, HardDrive, Globe, BarChart3,
} from "lucide-react";

const errMsg = (e, fb) => e?.response?.data?.detail || fb;
const fmtEpoch = (v) => {
  const n = Number(v);
  if (!n) return "—";
  return new Date(n * 1000).toLocaleDateString("en-AU",
    { day: "2-digit", month: "short", year: "numeric" });
};
const fmtIso = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-AU", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

export default function ClientHostingPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/hosting/clients/${clientId}/accounts`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e, "Failed to load hosting accounts")))
      .finally(() => setLoading(false));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const accounts = data?.accounts || [];
  const servers = data?.servers || [];
  const errors = data?.errors || [];

  return (
    <div className="space-y-5 max-w-6xl" data-testid="client-hosting-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs label-overline text-[#002FA7]">
            <Server className="h-3 w-3" /> Hosting
          </div>
          <p className="text-sm text-zinc-500 mt-1">
            Every cPanel account mapped to this client, across all WHM servers.
            Click a domain for the full account details.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}
          disabled={loading} data-testid="client-hosting-refresh">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </header>

      {loading && !data && (
        <div className="p-8 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading hosting accounts…
        </div>
      )}

      {!loading && !accounts.length && !errors.length && (
        <Card>
          <CardContent className="p-10 text-center text-sm text-zinc-400"
            data-testid="client-hosting-empty">
            <Server className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No hosting accounts are mapped to this client yet. Map cPanel
            accounts to this client from <span className="font-semibold">Host Servers</span>.
          </CardContent>
        </Card>
      )}

      {!!errors.length && (
        <Card>
          <CardContent className="p-4">
            <div className="text-xs font-semibold flex items-center gap-1.5 text-amber-800 mb-2">
              <TriangleAlert className="h-3.5 w-3.5" /> Some servers couldn&apos;t respond
            </div>
            <ul className="text-xs text-zinc-600 space-y-1" data-testid="client-hosting-errors">
              {errors.map((e) => (
                <li key={e.server_id} className="flex gap-2">
                  <span className="font-mono text-zinc-400">{e.server_name || e.server_id}:</span>
                  <span>{e.error}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {!!servers.length && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
          data-testid="client-hosting-server-cards">
          {servers.map((s) => (
            <Card key={s.server_id} className="rounded-sm">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Server</div>
                <div className="text-sm font-bold truncate" title={s.server_host}>
                  {s.server_name || s.server_id}
                </div>
                <div className="text-[11px] text-zinc-400 truncate">{s.server_host}</div>
                <div className="text-xs mt-1">
                  <span className="font-semibold">{s.account_count}</span>
                  <span className="text-zinc-500"> account{s.account_count === 1 ? "" : "s"}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!!accounts.length && (
        <Card className="rounded-sm">
          <CardContent className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="client-hosting-table">
                <thead className="bg-zinc-50 text-zinc-500 text-left text-xs">
                  <tr>
                    <th className="p-2 font-medium">Domain</th>
                    <th className="p-2 font-medium">Server</th>
                    <th className="p-2 font-medium">User</th>
                    <th className="p-2 font-medium">Plan</th>
                    <th className="p-2 font-medium">Disk</th>
                    <th className="p-2 font-medium">Since</th>
                    <th className="p-2 font-medium">AWStats</th>
                    <th className="p-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={`${a.server_id}-${a.user}`}
                      className="border-t hover:bg-zinc-50/50"
                      data-testid={`client-hosting-row-${a.user}`}>
                      <td className="p-2">
                        <button
                          className="font-medium text-sky-700 hover:text-sky-900 hover:underline text-left flex items-center gap-1"
                          onClick={() => navigate(
                            `/host-servers/${a.server_id}/accounts/${a.user}`,
                            { state: { from: `/clients/${clientId}/hosting`,
                                       backLabel: "Client hosting" } })}
                          data-testid={`client-hosting-link-${a.user}`}>
                          <Globe className="h-3 w-3" /> {a.domain || "—"}
                        </button>
                      </td>
                      <td className="p-2 text-xs">
                        <div className="font-medium">{a.server_name}</div>
                        <div className="text-[10px] text-zinc-400 font-mono">{a.server_host}</div>
                      </td>
                      <td className="p-2 font-mono text-xs">{a.user}</td>
                      <td className="p-2 text-xs">{a.plan || "—"}</td>
                      <td className="p-2 text-xs flex items-center gap-1">
                        <HardDrive className="h-3 w-3 text-zinc-300" />
                        {a.diskused || "—"} / {a.disklimit || "—"}
                      </td>
                      <td className="p-2 text-xs">{fmtEpoch(a.startdate)}</td>
                      <td className="p-2">
                        {(() => {
                          const aw = a.awstats || {};
                          const enabled = aw.enabled_count || 0;
                          const mapped = aw.mapped_count || 0;
                          if (!mapped) {
                            return (
                              <Badge variant="outline"
                                className="rounded-sm text-[10px] text-zinc-400 border-zinc-200"
                                data-testid={`client-hosting-awstats-none-${a.user}`}>
                                not connected
                              </Badge>
                            );
                          }
                          const hasError = !!aw.last_error;
                          const badgeCls = hasError
                            ? "bg-red-50 text-red-700 border-red-200"
                            : enabled > 0
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-zinc-50 text-zinc-600 border-zinc-200";
                          return (
                            <div className="flex flex-col gap-0.5"
                              data-testid={`client-hosting-awstats-${a.user}`}>
                              <Badge variant="outline"
                                className={`rounded-sm text-[10px] flex items-center gap-1 w-fit ${badgeCls}`}
                                title={aw.last_error || undefined}>
                                <BarChart3 className="h-3 w-3" />
                                {hasError
                                  ? "error"
                                  : enabled > 0
                                    ? `pulling · ${enabled}/${mapped}`
                                    : `mapped · ${mapped}, paused`}
                              </Badge>
                              <span className="text-[10px] text-zinc-500">
                                Last pull: {fmtIso(aw.last_pulled_at)}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="p-2">
                        {a.suspended ? (
                          <Badge variant="outline"
                            className="bg-red-50 text-red-700 border-red-200 rounded-sm text-[10px]">
                            suspended
                          </Badge>
                        ) : (
                          <Badge variant="outline"
                            className="bg-emerald-50 text-emerald-700 border-emerald-200 rounded-sm text-[10px]">
                            active
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
